import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createAppWorkOrder,
  duplicateAppWorkOrder,
  fetchAppClients,
  fetchAppSites,
  fetchAppTechnicians,
  fetchAppWorkOrders,
  type AppWorkOrderFilters,
  type CreateAppWorkOrderInput,
  type UpdateAppWorkOrderInput,
  updateAppWorkOrder,
} from './appApi.ts';
import { MVP_QUERY_KEY_ROOT } from './useMvpData.ts';

export const APP_QUERY_KEY_ROOT = ['field-service-technique-app'];

export function useAppClients(enabled: boolean) {
  return useQuery({
    queryKey: [...APP_QUERY_KEY_ROOT, 'clients'],
    queryFn: ({ signal }) => fetchAppClients(signal),
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

export function useAppSites(clientId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [...APP_QUERY_KEY_ROOT, 'sites', clientId ?? null],
    queryFn: ({ signal }) => fetchAppSites(clientId, signal),
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

export function useAppTechnicians(enabled: boolean) {
  return useQuery({
    queryKey: [...APP_QUERY_KEY_ROOT, 'technicians'],
    queryFn: ({ signal }) => fetchAppTechnicians(signal),
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

export function useAppWorkOrders(filters: AppWorkOrderFilters, enabled: boolean) {
  return useQuery({
    queryKey: [...APP_QUERY_KEY_ROOT, 'work-orders', filters],
    queryFn: ({ signal }) => fetchAppWorkOrders(filters, signal),
    enabled,
    staleTime: 15_000,
    retry: false,
  });
}

export function useCreateAppWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateAppWorkOrderInput) => createAppWorkOrder(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: APP_QUERY_KEY_ROOT });
      await queryClient.invalidateQueries({ queryKey: MVP_QUERY_KEY_ROOT });
    },
  });
}

export function useUpdateAppWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workOrderId, payload }: { workOrderId: string; payload: UpdateAppWorkOrderInput }) =>
      updateAppWorkOrder(workOrderId, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: APP_QUERY_KEY_ROOT });
      await queryClient.invalidateQueries({ queryKey: MVP_QUERY_KEY_ROOT });
    },
  });
}

export function useDuplicateAppWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (workOrderId: string) => duplicateAppWorkOrder(workOrderId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: APP_QUERY_KEY_ROOT });
      await queryClient.invalidateQueries({ queryKey: MVP_QUERY_KEY_ROOT });
    },
  });
}
